import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "@/lib/adminApi";

/*
  AuthContext — thin wrapper around /api/auth/me.
  Server is the source of truth for identity + role. Client renders the
  admin surface only after /auth/me returns role === 'admin'.
*/

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = still checking, false = anonymous, object = signed in
  const [state, setState] = useState(null);

  const check = useCallback(async () => {
    try {
      const me = await authApi.me();
      setState(me);
    } catch {
      setState(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me if we are mid-oauth-callback (session_id fragment). AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      setState(false);
      return;
    }
    check();
  }, [check]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore network error on logout */
    }
    setState(false);
  }, []);

  const value = useMemo(
    () => ({
      loading: state === null,
      user: state && typeof state === "object" ? state : null,
      isAdmin: Boolean(state && typeof state === "object" && state.role === "admin"),
      refresh: check,
      logout
    }),
    [state, check, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
